import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { BaseService } from './base';
import { MenuGroup, MenuItem } from '../models';

@Injectable({
  providedIn: 'root',
})
export class MenuService extends BaseService {
  // Sent with the menu request so the API can translate user-entered names (a role's Menu
  // Group Name) server-side - $localize can't know them at build time.
  private readonly locale = inject(LOCALE_ID);
  private selectedMenuItem: MenuItem;

  private menuGroups: MenuGroup[];
  private currentMenuItems: MenuItem[] = [];

  constructor() {
    super('Menu');
  }

  // todo: always show until initial being hidden bug can be fixed
  // get hasMenuItems() { return this.currentMenuItems.length > 0; }
  readonly hasMenuItems = true;

  get isReadOnly(): boolean {
    return this.selectedMenuItem?.readOnly ?? false;
  }

  get menus(): Observable<MenuGroup[]> {
    if (this.menuGroups) {
      return of(this.menuGroups);
    }

    return this.httpService
      .get<MenuGroup[]>(`${this.apiUrl}?language=${this.locale}`)
      .pipe(tap((menus: MenuGroup[]) => (this.menuGroups = menus)));
  }

  loadMenuGroups(menuGroups: MenuGroup[]) {
    this.menuGroups = menuGroups;
  }

  menuItems(route: string): Observable<MenuItem[]> {
    return this.menus.pipe(
      map((menus: MenuGroup[]) => {
        // Match the group that CONTAINS the current route's item first, then fall back to the
        // group-route prefix. A role's Menu Group Name group holds items from several sections
        // (its own route is just the first item's), so only the item-level match finds it -
        // and the containing group is what carries the item's ReadOnly flag.
        const group =
          menus.find((menuGroup: MenuGroup) =>
            menuGroup.items.some((menuItem: MenuItem) => route.indexOf(menuItem.route) === 0),
          ) ?? menus.find((menuGroup: MenuGroup) => route.indexOf(menuGroup.route) === 0);
        this.currentMenuItems = group?.items ?? [];
        return this.currentMenuItems;
      }),
    );
  }

  menuItem(route: string): Observable<MenuItem> {
    return this.menuItems(route).pipe(
      map((menuItems: MenuItem[]) => {
        this.selectedMenuItem = menuItems.find((menuItem: MenuItem) => route.indexOf(menuItem.route) === 0);
        return this.selectedMenuItem;
      }),
    );
  }
}
